import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout, verifyLoginTOTP } from "../actions/userActions";
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import { Form, Input, Button, Typography, Space, Divider, Alert, Card } from 'antd';
import { WarningOutlined, CheckCircleTwoTone, InfoCircleOutlined } from "@ant-design/icons";
import { MdOutlineMailLock } from "react-icons/md";
import { SlScreenSmartphone } from "react-icons/sl";
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;
const { OTP } = Input;

function TwoFACodeRequestScreen() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [code, setCode] = useState('');
    const [TFAMessage, setTFAMessage] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [alertType, setAlertType] = useState('info');
    const [isTOTP, setIsTOTP] = useState(false);

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo, error } = userLogin;

    useEffect(() => {
        document.title = t('2FA.pageTitle');
    }, [t]);

    useEffect(() => {
        if (!userInfo || !userInfo.email) {
            navigate("/userlogin");
        } else {
            setEmail(userInfo.email);
            if (userInfo.requiresTOTP) {
                setIsTOTP(true);
                setTFAMessage(t('2FA.totpInstruction', { defaultValue: 'Enter the 6-digit code from your authenticator app' }));
                setAlertType('info');
            } else {
                handle2FACodeRequest(userInfo.email);
            }
        }
    }, [navigate, userInfo]);

    // Handle Redux state changes for TOTP verification
    useEffect(() => {
        if (isTOTP && !loading) {
            if (error) {
                setTFAMessage(error);
                setAlertType('error');
            } else if (userInfo && userInfo.token) {
                console.log('TOTP verification successful, userInfo:', userInfo);
                setTFAMessage(t('2FA.codeVerified'));
                setAlertType('success');
                setTimeout(() => navigate("/"), 2000);
            }
        }
    }, [error, userInfo, isTOTP, loading, navigate, t]);

    const handle2FACodeRequest = async (email) => {
        setSendingCode(true);
        setTFAMessage(t('2FA.sendingCode', { email }));
        setAlertType('info');
        try {
            await axios.post(`/dotnet_api/v1/auth/sendverificationcode`, { email });
            setSendingCode(false);
            setTFAMessage(t('2FA.codeSent', { email }));
            setAlertType('success');
        } catch (error) {
            setSendingCode(false);
            setTFAMessage(t('2FA.codeRequestFailed'));
            setAlertType('error');
        }
    }

    const handle2FACodeSubmission = async (values) => {
        setLoading(true);
        try {
            if (isTOTP) {
                await dispatch(verifyLoginTOTP(email, values.code));
                setLoading(false);
            } else {
                await axios.post(`/dotnet_api/v1/auth/verifycode`, { email, code: values.code });
                setLoading(false);
                setTFAMessage(t('2FA.codeVerified'));
                setAlertType('success');
                localStorage.setItem("2fauthenticated", "true");
                setTimeout(() => navigate("/"), 2000);
            }
        } catch (error) {
            setLoading(false);
            setTFAMessage(t('2FA.codeVerificationFailed'));
            setAlertType('error');
        }
    }

    const handleLogout = () => {
        dispatch(logout());
        navigate("/userlogin");
    };


    const getAlertIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircleTwoTone twoToneColor="#52c41a" />;
            case 'error':
                return <WarningOutlined style={{ color: '#ff4d4f' }} />;
            case 'info':
            default:
                return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
        }
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 32 }}>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        {isTOTP ? (
                            <SlScreenSmartphone size={64} style={{  marginBottom: 16 }} />
                        ) : (
                            <MdOutlineMailLock size={64} style={{ marginBottom: 16 }} />
                        )}
                        <Title level={2} style={{ marginBottom: 0 }}>
                            {t('2FA.title')}
                        </Title>
                    </div>
                    <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                        {t('2FA.instruction')}
                    </Text>
                    <Text type="secondary" style={{ textAlign: 'center', display: 'block', marginBottom: 16 }}>
                        <strong>{userInfo?.email}</strong>
                    </Text>
                    <Form
                        onFinish={handle2FACodeSubmission}
                        style={{ width: '100%' }}
                    >
                        <Form.Item
                            name="code"
                            rules={[{ required: true, message: t('2FA.codeRequired') }]}
                            style={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <OTP
                                size="large"
                                length={6}
                                placeholder={t('2FA.codePlaceholder')}
                                onChange={(value) => setCode(value)}
                                value={code}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Button
                                type="primary"
                                size="large"
                                htmlType="submit"
                                style={{ width: '100%' }}
                                loading={loading}
                            >
                                {loading ? t('2FA.verifying') : t('2FA.verify')}
                            </Button>
                        </Form.Item>
                    </Form>
                    {TFAMessage && (
                        <Alert
                            message={TFAMessage}
                            type={alertType}
                            showIcon
                            icon={getAlertIcon(alertType)}
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    {!isTOTP && (
                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                            <a href="#" onClick={() => handle2FACodeRequest(email)} disabled={sendingCode}>
                                {sendingCode ? t('2FA.sendingCode') : t('2FA.resendCode')}
                            </a>
                        </div>
                    )}
                    <Divider />
                    <div style={{ textAlign: 'center', marginTop: '8px' }}>
                        <Link to="/" onClick={handleLogout}>
                            {t('2FA.logout')}
                        </Link>
                    </div>
                </Card>
            </Space>
    );
}

export default TwoFACodeRequestScreen;